import {
    Component,
    ElementRef,
    Injector,
    OnInit,
    ViewChild,
    afterNextRender,
    computed,
    inject,
    signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import type { Observable } from 'rxjs';
import { WorkflowGateway } from '../../../gateways/workflow.gateway';
import type { Workflow } from '../../../models/workflow.model';
import { AuthService } from '../../../services/auth.service';
import { editorLabel, lifecycleLabel, publicationLabel } from './editor/flow-presentation';
import { WorkflowCreateDialog } from './workflow-create-dialog';

type ListState = 'loading' | 'ready' | 'empty' | 'error';

const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number, number]> = [
    ['minute', 60, 60],
    ['hour', 24, 3600],
    ['day', 7, 86_400],
    ['week', 5, 604_800],
];

/**
 * A short relative time for an ISO 8601 instant ("just now", "5 minutes ago", "yesterday",
 * "3 weeks ago"); older than ~a month falls back to the formatted date. `''` for a blank or
 * unparseable value (null object: the caller just omits the time).
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
    const at = Date.parse(iso);
    if (!iso || Number.isNaN(at)) {
        return '';
    }
    const seconds = (now - at) / 1000;
    if (seconds < 45) {
        return 'just now';
    }
    const format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    // Each entry: the unit, how many of it still read naturally, and its size in seconds.
    for (const [unit, limit, size] of RELATIVE_UNITS) {
        const amount = Math.max(1, Math.round(seconds / size));
        if (amount < limit) {
            return format.format(-amount, unit);
        }
    }
    return `on ${new Date(at).toLocaleDateString('en', { dateStyle: 'medium' })}`;
}

/** Newest draft edit first (17.2); unparseable timestamps sort last. */
function byEditedAtDesc(a: Workflow, b: Workflow): number {
    const at = (w: Workflow) => {
        const value = Date.parse(w.draft.editedAt);
        return Number.isNaN(value) ? -Infinity : value;
    };
    const [left, right] = [at(a), at(b)];
    return left === right ? 0 : right > left ? 1 : -1;
}

@Component({
    selector: 'app-workflows',
    standalone: true,
    imports: [RouterLink, WorkflowCreateDialog],
    template: `
        <div class="page">
            <div class="head">
                <div>
                    <h1>Workflows</h1>
                    <p>Design how agents and roles work together in this project.</p>
                </div>
                <button
                    type="button"
                    class="btn btn-primary"
                    data-testid="workflow-new"
                    (click)="openCreate()"
                >
                    New workflow
                </button>
            </div>

            @if (state() === 'loading') {
                <div class="state" role="status" aria-live="polite" data-testid="workflows-loading">
                    Loading workflows…
                </div>
            }

            @if (state() === 'error') {
                <div class="state state-error" role="alert" data-testid="workflows-error">
                    <p>{{ errorMessage() }}</p>
                    <button type="button" class="btn" (click)="load()">Retry</button>
                </div>
            }

            @if (state() === 'empty') {
                <div class="state" role="status" data-testid="workflows-empty">
                    <p>No workflows yet.</p>
                    <button
                        type="button"
                        class="btn btn-primary"
                        data-testid="workflow-create-first"
                        (click)="openCreate()"
                    >
                        Create your first workflow
                    </button>
                </div>
            }

            @if (rowError()) {
                <div class="state state-error" role="alert" data-testid="workflows-row-error">
                    {{ rowError() }}
                </div>
            }

            @if (state() === 'ready') {
                <ul class="list" aria-label="Workflows">
                    @for (wf of sorted(); track wf.id) {
                        <li
                            class="row"
                            [class.archived]="wf.lifecycle === 'archived'"
                            [attr.data-testid]="'workflow-row-' + wf.id"
                        >
                            <div class="info">
                                <a class="name" [routerLink]="['../workflows', wf.id]">{{
                                    wf.name
                                }}</a>
                                <p class="desc">{{ wf.description }}</p>
                                <div class="meta">
                                    <span
                                        class="badge"
                                        [class.badge-ok]="wf.publicationState === 'published'"
                                        [class.badge-warn]="
                                            wf.publicationState === 'published-with-changes'
                                        "
                                        data-testid="workflow-publication"
                                        >{{ publication(wf) }}</span
                                    >
                                    @if (wf.lifecycle === 'archived') {
                                        <span class="badge" data-testid="workflow-archived">{{
                                            lifecycle(wf)
                                        }}</span>
                                    }
                                    <span class="badge" data-testid="workflow-release">{{
                                        release(wf)
                                    }}</span>
                                    <span class="edited" data-testid="workflow-edited">{{
                                        lastEdited(wf)
                                    }}</span>
                                </div>
                            </div>
                            <div class="actions">
                                @if (wf.lifecycle === 'active') {
                                    <button
                                        type="button"
                                        class="btn btn-sm"
                                        data-testid="workflow-archive"
                                        [attr.aria-label]="'Archive ' + wf.name"
                                        [disabled]="busyId() === wf.id"
                                        (click)="archive(wf)"
                                    >
                                        Archive
                                    </button>
                                } @else {
                                    <button
                                        type="button"
                                        class="btn btn-sm"
                                        data-testid="workflow-restore"
                                        [attr.aria-label]="'Restore ' + wf.name"
                                        [disabled]="busyId() === wf.id"
                                        (click)="restore(wf)"
                                    >
                                        Restore
                                    </button>
                                }
                                @if (canDelete(wf)) {
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-danger"
                                        data-testid="workflow-delete"
                                        [attr.aria-label]="'Delete ' + wf.name"
                                        [disabled]="busyId() === wf.id"
                                        (click)="requestDelete(wf)"
                                    >
                                        Delete
                                    </button>
                                }
                            </div>
                        </li>
                    }
                </ul>
            }
        </div>

        @if (creating()) {
            <app-workflow-create-dialog
                [projectId]="projectId"
                (created)="onCreated($event)"
                (cancelled)="closeCreate()"
            />
        }

        @if (pendingDelete(); as target) {
            <div class="backdrop" (click)="cancelDelete()"></div>
            <div
                #dialog
                class="dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="workflow-delete-title"
                data-testid="workflow-delete-dialog"
                (keydown)="onDialogKeydown($event)"
            >
                <h2 id="workflow-delete-title">Delete workflow</h2>
                <p>Delete "{{ target.name }}"? This cannot be undone.</p>
                <div class="dialog-actions">
                    <button #cancelBtn type="button" class="btn" (click)="cancelDelete()">
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        data-testid="workflow-delete-confirm"
                        (click)="confirmDelete()"
                    >
                        Delete
                    </button>
                </div>
            </div>
        }
    `,
    styles: [
        `
            .page {
                padding: 2rem;
                color: hsl(var(--color-text-main));
            }
            .head {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 1rem;
                margin-bottom: 1.5rem;
            }
            h1 {
                font-size: 1.75rem;
                font-weight: 600;
                margin: 0 0 0.5rem;
            }
            p {
                color: hsl(var(--color-text-muted));
                margin: 0;
            }
            .state {
                padding: 2rem;
                margin-bottom: 1rem;
                background: hsl(var(--color-bg-card));
                border: 1px solid hsl(var(--border-color));
                border-radius: var(--radius-md);
                text-align: center;
                color: hsl(var(--color-text-muted));
            }
            .state-error,
            .state-error p {
                background: hsl(var(--color-danger-bg));
                color: hsl(var(--color-danger));
            }
            .state p {
                margin-bottom: 1rem;
            }
            .list {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 0.75rem;
            }
            .row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 1rem;
                padding: 1rem 1.25rem;
                background: hsl(var(--color-bg-card));
                border: 1px solid hsl(var(--border-color));
                border-radius: var(--radius-md);
            }
            .row.archived {
                background: hsl(var(--color-bg-base));
            }
            .row.archived .info {
                opacity: 0.65;
            }
            .name {
                font-weight: 600;
                font-size: 1.05rem;
                color: hsl(var(--color-text-main));
                text-decoration: none;
            }
            .name:hover {
                color: hsl(var(--color-primary));
            }
            .desc {
                margin: 0.25rem 0 0.5rem;
            }
            .meta {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.5rem;
                font-size: 0.8rem;
            }
            .badge {
                padding: 0.15rem 0.5rem;
                border-radius: 999px;
                background: hsl(var(--border-color));
            }
            .badge-ok {
                background: hsl(var(--color-success-bg));
                color: hsl(var(--color-success));
            }
            .badge-warn {
                background: hsl(var(--color-warning-bg));
                color: hsl(var(--color-warning));
            }
            .edited {
                color: hsl(var(--color-text-muted));
            }
            .actions,
            .dialog-actions {
                display: flex;
                gap: 0.5rem;
                flex-shrink: 0;
            }
            .btn {
                padding: 0.5rem 0.9rem;
                border-radius: var(--radius-sm);
                border: 1px solid hsl(var(--border-color));
                background: hsl(var(--color-bg-card));
                color: hsl(var(--color-text-main));
                font-size: 0.9rem;
            }
            .btn:disabled {
                opacity: 0.6;
                cursor: default;
            }
            .btn:focus-visible,
            .name:focus-visible {
                outline: 2px solid hsl(var(--color-focus-ring));
                outline-offset: 2px;
            }
            .btn-primary {
                background: hsl(var(--color-primary));
                border-color: hsl(var(--color-primary));
                color: hsl(var(--color-bg-card));
            }
            .btn-danger {
                color: hsl(var(--color-danger));
            }
            .btn-sm {
                padding: 0.35rem 0.7rem;
                font-size: 0.85rem;
            }
            .backdrop {
                position: fixed;
                inset: 0;
                background: hsl(var(--color-text-main) / 0.35);
            }
            .dialog {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: min(90vw, 420px);
                padding: 1.5rem;
                background: hsl(var(--color-bg-card));
                border-radius: var(--radius-md);
                box-shadow: var(--shadow-lg);
            }
            .dialog h2 {
                margin: 0 0 0.75rem;
                font-size: 1.25rem;
            }
            .dialog-actions {
                justify-content: flex-end;
                margin-top: 1.5rem;
            }
        `,
    ],
})
export class Workflows implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly gateway = inject(WorkflowGateway);
    private readonly auth = inject(AuthService);
    private readonly injector = inject(Injector);

    @ViewChild('cancelBtn') private cancelBtn?: ElementRef<HTMLButtonElement>;
    @ViewChild('dialog') private dialog?: ElementRef<HTMLElement>;

    readonly state = signal<ListState>('loading');
    readonly workflows = signal<Workflow[]>([]);
    readonly sorted = computed(() => [...this.workflows()].sort(byEditedAtDesc));
    readonly errorMessage = signal('');
    readonly rowError = signal('');
    readonly busyId = signal('');
    readonly creating = signal(false);
    readonly pendingDelete = signal<Workflow | null>(null);

    projectId = '';
    private triggerElement: HTMLElement | null = null;

    ngOnInit(): void {
        this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
        this.load();
    }

    load(): void {
        this.state.set('loading');
        this.errorMessage.set('');
        this.rowError.set('');
        this.gateway.listWorkflows(this.projectId).subscribe({
            next: (workflows) => {
                this.workflows.set(workflows);
                this.state.set(workflows.length ? 'ready' : 'empty');
            },
            error: (err: Error) => {
                this.errorMessage.set(err.message);
                this.state.set('error');
            },
        });
    }

    publication(wf: Workflow): string {
        return publicationLabel(wf.publicationState);
    }

    lifecycle(wf: Workflow): string {
        return lifecycleLabel(wf.lifecycle);
    }

    release(wf: Workflow): string {
        return wf.latestRelease.number > 0 ? `Release ${wf.latestRelease.number}` : 'Not released';
    }

    /** "Last edited <relative time> by <editor label>" (17.2, 22.3). */
    lastEdited(wf: Workflow): string {
        const me = this.auth.user()?.sub ?? '';
        const when = relativeTime(wf.draft.editedAt);
        const who = editorLabel(wf.draft.editedBy, me);
        return when ? `Last edited ${when} by ${who}` : `Last edited by ${who}`;
    }

    /** Delete is offered for archived or never-published workflows (17.5, mirroring 2.6). */
    canDelete(wf: Workflow): boolean {
        return wf.lifecycle === 'archived' || wf.publicationState === 'draft';
    }

    openCreate(): void {
        this.triggerElement = document.activeElement as HTMLElement | null;
        this.creating.set(true);
    }

    closeCreate(): void {
        this.creating.set(false);
        this.restoreFocus();
    }

    onCreated(wf: Workflow): void {
        this.creating.set(false);
        this.triggerElement = null;
        this.router.navigate(['../workflows', wf.id], { relativeTo: this.route });
    }

    archive(wf: Workflow): void {
        this.runRowAction(wf, this.gateway.archiveWorkflow(this.projectId, wf.id));
    }

    restore(wf: Workflow): void {
        this.runRowAction(wf, this.gateway.restoreWorkflow(this.projectId, wf.id));
    }

    requestDelete(wf: Workflow): void {
        this.rowError.set('');
        this.triggerElement = document.activeElement as HTMLElement | null;
        this.pendingDelete.set(wf);
        // Move focus into the dialog once it renders (zoneless: a microtask runs too early).
        afterNextRender(() => this.cancelBtn?.nativeElement.focus(), { injector: this.injector });
    }

    cancelDelete(): void {
        this.pendingDelete.set(null);
        this.restoreFocus();
    }

    confirmDelete(): void {
        const target = this.pendingDelete();
        if (!target) {
            return;
        }
        this.pendingDelete.set(null);
        this.restoreFocus();
        this.busyId.set(target.id);
        this.gateway.deleteWorkflow(this.projectId, target.id).subscribe({
            next: () => {
                this.busyId.set('');
                this.workflows.update((list) => list.filter((w) => w.id !== target.id));
                if (!this.workflows().length) {
                    this.state.set('empty');
                }
            },
            error: (err: Error) => {
                this.busyId.set('');
                this.rowError.set(err.message);
            },
        });
    }

    onDialogKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.cancelDelete();
            return;
        }
        if (event.key === 'Tab') {
            this.trapFocus(event);
        }
    }

    /** Archive / Restore: replace the row with the returned `WorkflowResponse` (17.4). */
    private runRowAction(wf: Workflow, request: Observable<Workflow>): void {
        this.rowError.set('');
        this.busyId.set(wf.id);
        request.subscribe({
            next: (updated) => {
                this.busyId.set('');
                this.workflows.update((list) =>
                    list.map((w) => (w.id === updated.id ? updated : w)),
                );
            },
            error: (err: Error) => {
                this.busyId.set('');
                this.rowError.set(err.message);
            },
        });
    }

    private trapFocus(event: KeyboardEvent): void {
        const host = this.dialog?.nativeElement;
        if (!host) {
            return;
        }
        const focusable = host.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea',
        );
        if (!focusable.length) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }

    private restoreFocus(): void {
        this.triggerElement?.focus();
        this.triggerElement = null;
    }
}
